import { useState, useCallback } from 'react';
import { ExternalLinkIcon, CopyIcon } from '@rsos/assets/icons';
import { RemoveCircleIcon } from '@rsos/base-assets/icons';
import { Button, Text, Box, Modal } from '@rsos/base-components';
import {
  ModalHeader,
  ModalFooter,
} from '@rsos/base-components/Modal/ModalComponents';
import { useDeleteEventShareMutation } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { getConnectShareUrl } from '../../../../../../utils/getConnectShareUrl';
import AdditionalComments from './AdditionalComments';
import DataShared from './DataShared';
import { AccordionContentWrapper } from './EventShareAccordionContent.styles';
import { ShareLinkContainer } from './EventShareAccordionContent.styles';
import Recipients from './Recipients/DisplayDeleteRecipients';

const EventShareAccordionContent = ({ share }: { share: EventShare }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const orgName = useCurrentOrgName();
  const [deleteEventShare] = useDeleteEventShareMutation();
  const [isConfirming, setIsConfirming] = useState(false);

  const isActive = share.status === 'active';
  const isShareLink = share.share_type === 'link';

  const shareLink =
    isShareLink && share.recipients.sender?.share_key
      ? getConnectShareUrl(share.recipients.sender.share_key)
      : null;

  const handleCopyLink = useCallback(() => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      notifySuccess('Share link copied to clipboard');
    }
  }, [shareLink]);

  const handleStopSharing = async () => {
    if (!orgName || !share.session_id) return;

    setIsDeleting(true);
    try {
      await deleteEventShare({
        org: orgName,
        sessionId: share.session_id,
        uri: share.uri,
      }).unwrap();

      notifySuccess('Sharing stopped successfully');
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'Failed to stop sharing');
      notifyError(errorMessage);
    } finally {
      setIsConfirming(false);
      setIsDeleting(false);
    }
  };
  const toggleConfirmationModal = () => {
    setIsConfirming(!isConfirming);
  };

  return (
    <>
      <AccordionContentWrapper>
        {isShareLink && shareLink && (
          <Box>
            <Box
              horizontal
              flow={4}
              align="center"
              color="primaryText"
              mb="8px"
            >
              <ExternalLinkIcon height={12} width={12} />
              <Text fontWeight="bold" size="normal">
                Share Link
              </Text>
            </Box>
            <Box bg="foreground" p={16} borderRadius="4px">
              <ShareLinkContainer>
                <Text size="normal" color="secondaryText">
                  {shareLink}
                </Text>
              </ShareLinkContainer>

              {isActive && (
                <Button
                  btnType="primary"
                  size="small"
                  noMargin
                  onClick={handleCopyLink}
                >
                  <Box horizontal align="center" flow={4}>
                    <CopyIcon width={12} height={12} />
                    <Text size="small">Copy Link</Text>
                  </Box>
                </Button>
              )}
              <Text size="small" color="secondaryText" mt="12px">
                Anyone with this link can view event details
              </Text>
            </Box>
          </Box>
        )}
        {!isShareLink && <Recipients share={share} isActive={isActive} />}
        <DataShared share={share} />
        <AdditionalComments share={share} isActive={isActive} />
      </AccordionContentWrapper>
      {isActive && (
        <Box horizontal width="100%" p={16}>
          <Button
            btnType="warning_tertiary"
            size="small"
            noMargin
            grow
            onClick={toggleConfirmationModal}
            disabled={isDeleting}
          >
            <Box horizontal align="center" flow={4}>
              <RemoveCircleIcon color="red" width={12} height={12} />
              <Text size="small">Stop Sharing</Text>
            </Box>
          </Button>
        </Box>
      )}
      <Modal
        isOpened={isConfirming}
        onClose={toggleConfirmationModal}
        width={350}
        id="stop-sharing-modal"
      >
        <ModalHeader onClose={toggleConfirmationModal}>
          <Text size="large" color="primaryText">
            Are you sure you want to stop sharing this event?
          </Text>
          <Text size="small" color="secondaryText" mt="8px">
            All recipients will lose access to the shared data once this link is
            canceled.
          </Text>
        </ModalHeader>
        <ModalFooter stacked flow={12}>
          <Button
            btnType="warning_primary"
            onClick={handleStopSharing}
            grow
            noMargin
            isLoadingProp={isDeleting}
          >
            Stop Sharing
          </Button>
          <Button
            btnType="tertiary"
            onClick={toggleConfirmationModal}
            grow
            noMargin
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EventShareAccordionContent;
